import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FiCalendar, FiMapPin } from "react-icons/fi";
import { MdOutlineEvent } from "react-icons/md";
import { MyContext } from "../../Context/MyProvider";
import noImage from "../../Images/no_image.png";
import ThemedPageFrame from "./ThemedPageFrame";

const STATIC_LOCATION = {
    lat: 26.9124,
    long: 75.7873,
};

const EVENT_SECTIONS = [
    { key: "latest_event", title: "Latest Events", subtitle: "Freshly added experiences and live listings." },
    { key: "nearby_event", title: "Nearby Events", subtitle: "Events available around your selected location." },
    { key: "this_month_event", title: "This Month", subtitle: "Plan your calendar with this month's highlights." },
    { key: "upcoming_event", title: "Upcoming Events", subtitle: "Book ahead for the next big thing." },
];

const Events = () => {
    const { BaseUrl } = useContext(MyContext);
    const navigate = useNavigate();
    const [homeData, setHomeData] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchEvents = async () => {
            const userId = localStorage.getItem("Userid") || "0";

            setLoading(true);
            try {
                const res = await axios.post(
                    `${BaseUrl}user_api/u_home_data.php`,
                    {
                        uid: userId,
                        lats: STATIC_LOCATION.lat,
                        longs: STATIC_LOCATION.long,
                    },
                    {
                        headers: {
                            "Content-Type": "application/json",
                        },
                    }
                );

                setHomeData(res?.data?.HomeData || {});
            } catch {
                setHomeData({});
            } finally {
                setLoading(false);
            }
        };

        fetchEvents();
    }, [BaseUrl]);

    const sections = useMemo(
        () =>
            EVENT_SECTIONS.map((section) => ({
                ...section,
                events: Array.isArray(homeData?.[section.key]) ? homeData[section.key] : [],
            })),
        [homeData]
    );

    const totalEvents = useMemo(
        () => sections.reduce((total, section) => total + section.events.length, 0),
        [sections]
    );

    const getEventImage = (event) => {
        const image = event?.event_img || event?.image || event?.cover_img;

        if (!image) return noImage;
        if (String(image).startsWith("http") || String(image).startsWith("/")) return image;

        return `${BaseUrl}${image}`;
    };

    const openEvent = (event) => {
        if (!event?.slug) return;

        sessionStorage.setItem("Eventid", event?.event_id || "");
        sessionStorage.setItem("EventSlug", event.slug);
        navigate(`/event-details/${event.slug}`);
    };

    const renderSkeleton = () => (
        <div className="events_page_grid">
            {[1, 2, 3, 4, 5, 6].map((item) => (
                <div key={item} className="events_page_card events_page_card_skeleton">
                    <span></span>
                    <div></div>
                    <p></p>
                    <small></small>
                </div>
            ))}
        </div>
    );

    const summary = (
        <>
            <span>Total Events</span>
            <strong>{loading ? "--" : totalEvents}</strong>
        </>
    );

    return (
        <ThemedPageFrame
            activePath="/events"
            eyebrow="Nabhiverse Events"
            title="Events"
            subtitle="Explore every event collection in one place, from latest listings to upcoming experiences."
            summary={summary}
            className="events_page_layout"
        >
            <div className="events_page_shell">
                {loading ? (
                    renderSkeleton()
                ) : totalEvents === 0 ? (
                    <div className="events_page_empty">
                        <MdOutlineEvent />
                        <h2>No events available</h2>
                        <p>Please check back later.</p>
                    </div>
                ) : (
                    sections.map((section) => {
                        if (section.events.length === 0) return null;

                        return (
                            <section key={section.key} className="events_page_section">
                                <div className="events_page_section_head">
                                    <div>
                                        <h2>{section.title}</h2>
                                        <p>{section.subtitle}</p>
                                    </div>
                                    <span>{section.events.length} Events</span>
                                </div>

                                <div className="events_page_grid">
                                    {section.events.map((event, index) => (
                                        <button
                                            key={event?.event_id || event?.slug || `${section.key}-${index}`}
                                            type="button"
                                            className="events_page_card"
                                            onClick={() => openEvent(event)}
                                        >
                                            <span className="events_page_badge">Event</span>
                                            <img src={getEventImage(event)} alt={event?.event_title || "Event"} />
                                            <div className="events_page_card_body">
                                                <strong>{event?.event_title || "Nabhiverse Event"}</strong>
                                                <p><FiMapPin /> {event?.event_place_name || "Nabhiverse"}</p>
                                                <small><FiCalendar /> {event?.event_sdate || "Date announced soon"}</small>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </section>
                        );
                    })
                )}
            </div>
        </ThemedPageFrame>
    );
};

export default Events;
